import React, {useState, useEffect} from "react";
import {httpsCallable} from "firebase/functions";
import {functions} from "../../services/firebase/config";

import {convertToDate} from "../../utils/dateUtils";
import "../../styles/env.css";

interface DMApproval {
  id: string;
  userId: string;
  displayName: string;
  email: string;
  requestDate: any;
  status: "pending" | "approved" | "rejected";
  reviewedBy?: string;
  reviewedAt?: any;
}

const DMApprovals: React.FC = () => {
  const [approvals, setApprovals] = useState<DMApproval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingApproval, setProcessingApproval] = useState<string | null>(
    null
  );

  // Cloud Functions
  const getPendingDMApprovals = httpsCallable(
    functions,
    "getPendingDMApprovals"
  );
  const approveDMRequest = httpsCallable(functions, "approveDMRequest");

  useEffect(() => {
    loadPendingApprovals();
  }, []);

  const loadPendingApprovals = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await getPendingDMApprovals();
      const data = result.data as {success: boolean; approvals: DMApproval[]};

      if (data.success) {
        setApprovals(data.approvals);
      } else {
        setError("Failed to load pending approvals");
      }
    } catch (err: any) {
      // Error handling for pending approvals loading operations
      setError(err.message || "Failed to load pending approvals");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId: string, approved: boolean) => {
    try {
      setProcessingApproval(userId);
      setError(null);

      await approveDMRequest({userId, approved});

      // Remove the approval from the list since it's no longer pending
      setApprovals((prev) =>
        prev.filter((approval) => approval.userId !== userId)
      );

      // DM request processed successfully
    } catch (err: any) {
      // Error handling for approval processing operations
      setError(err.message || "Failed to process approval");
    } finally {
      setProcessingApproval(null);
    }
  };

  // Helper to truncate text and show full value on hover
  function truncateWithTooltip(text: string, maxLength: number) {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + "…";
  }

  const formatDate = (timestamp: any) => {
    if (!timestamp) return "N/A";

    const date = convertToDate(timestamp);
    if (isNaN(date.getTime())) return "N/A";

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="env-page-container">
        <h2 className="env-page-title">DM Approvals</h2>
        <section className="env-section">
          <div className="env-section-header">PENDING APPROVALS</div>
          <div className="env-loading-state">
            <div className="loading-spinner"></div>
            <p>Loading pending approvals...</p>
          </div>
        </section>
      </div>
    );
  }

  if (error) {
    return (
      <div className="env-page-container">
        <h2 className="env-page-title">DM Approvals</h2>
        <section className="env-section">
          <div className="env-section-header">PENDING APPROVALS</div>
          <div className="env-error-state">
            <p>Error: {error}</p>
            <button className="btn btn-primary" onClick={loadPendingApprovals}>
              Retry
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="env-page-container">
      <h2 className="env-page-title">DM Approvals</h2>
      <section className="env-section">
        <div className="env-section-header">
          PENDING APPROVALS ({approvals.length})
        </div>

        {approvals.length === 0 ? (
          <div className="env-empty-state">
            <p>No pending DM approvals</p>
          </div>
        ) : (
          <div className="env-user-table-wrapper env-user-table-border">
            <table className="env-user-table">
              <thead>
                <tr>
                  <th>Display Name</th>
                  <th>Email</th>
                  <th>Request Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {approvals.map((approval) => (
                  <tr key={approval.id} className="env-user-row">
                    <td
                      title={approval.displayName}
                      className="truncate-cell username-cell"
                    >
                      {truncateWithTooltip(approval.displayName, 10)}
                    </td>
                    <td
                      title={approval.email}
                      className="truncate-cell email-cell"
                    >
                      {truncateWithTooltip(approval.email, 24)}
                    </td>
                    <td className="date-cell">
                      {formatDate(approval.requestDate)}
                    </td>
                    <td>
                      <div className="env-approval-actions">
                        <button
                          className="btn btn-success env-approval-btn"
                          onClick={() => handleApprove(approval.userId, true)}
                          disabled={processingApproval === approval.userId}
                        >
                          {processingApproval === approval.userId
                            ? "Approving..."
                            : "Approve"}
                        </button>
                        <button
                          className="btn btn-destructive env-approval-btn"
                          onClick={() => handleApprove(approval.userId, false)}
                          disabled={processingApproval === approval.userId}
                        >
                          {processingApproval === approval.userId
                            ? "Rejecting..."
                            : "Reject"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default DMApprovals;
